import React, {
  Children,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { AiOutlineLeft, AiOutlineRight } from 'react-icons/ai';
import { cx } from './hooks';

/**
 * Slider. Each child is one slide.
 * Ref methods: next(), prev(), goTo(index).
 * effect: 'slide' (default) | 'fade'
 */
const Carousel = forwardRef(
  (
    {
      children,
      autoplay = false,
      autoplaySpeed = 3000,
      dots = true,
      arrows = false,
      effect = 'slide',
      infinite = true,
      pauseOnHover = true,
      afterChange,
      beforeChange,
      className,
      style,
      dotsClassName,
    },
    ref
  ) => {
    const slides = Children.toArray(children).filter(Boolean);
    const count = slides.length;
    const [index, setIndex] = useState(0);
    const [paused, setPaused] = useState(false);
    const pointerStart = useRef(null);

    const goTo = useCallback(
      target => {
        if (!count) return;
        let next = target;
        if (infinite) next = ((target % count) + count) % count;
        else next = Math.min(Math.max(target, 0), count - 1);
        if (next === index) return;
        beforeChange?.(index, next);
        setIndex(next);
      },
      [count, infinite, beforeChange, index]
    );

    const next = useCallback(() => goTo(index + 1), [goTo, index]);
    const prev = useCallback(() => goTo(index - 1), [goTo, index]);

    useImperativeHandle(ref, () => ({ next, prev, goTo }), [next, prev, goTo]);

    const firstRender = useRef(true);
    useEffect(() => {
      if (firstRender.current) {
        firstRender.current = false;
        return;
      }
      afterChange?.(index);
      // afterChange only needs to run when the slide changes
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [index]);

    useEffect(() => {
      if (index >= count && count > 0) setIndex(count - 1);
    }, [count, index]);

    useEffect(() => {
      if (!autoplay || paused || count < 2) return undefined;
      const timer = setInterval(() => goTo(index + 1), autoplaySpeed);
      return () => clearInterval(timer);
    }, [autoplay, paused, count, autoplaySpeed, goTo, index]);

    if (!count) return null;

    const onPointerDown = e => {
      pointerStart.current = e.clientX;
    };
    const onPointerUp = e => {
      if (pointerStart.current === null) return;
      const delta = e.clientX - pointerStart.current;
      pointerStart.current = null;
      if (Math.abs(delta) > 50) {
        if (delta < 0) next();
        else prev();
      }
    };

    const fade = effect === 'fade';

    return (
      <div
        className={cx('carousel', fade && 'is-fade', className)}
        style={style}
        onMouseEnter={pauseOnHover ? () => setPaused(true) : undefined}
        onMouseLeave={pauseOnHover ? () => setPaused(false) : undefined}
        onPointerDown={onPointerDown}
        onPointerUp={onPointerUp}
        onPointerCancel={() => {
          pointerStart.current = null;
        }}
        aria-roledescription="carousel"
      >
        <div
          className="carousel-track"
          style={fade ? undefined : { transform: `translateX(-${index * 100}%)` }}
        >
          {slides.map((slide, i) => (
            <div
              key={slide.key ?? i}
              className={cx('carousel-slide', i === index && 'is-active')}
              aria-hidden={i !== index}
              aria-roledescription="slide"
            >
              {slide}
            </div>
          ))}
        </div>

        {arrows && count > 1 && (
          <>
            <button
              type="button"
              className="carousel-arrow is-prev"
              onClick={prev}
              disabled={!infinite && index === 0}
              aria-label="Previous slide"
            >
              <AiOutlineLeft />
            </button>
            <button
              type="button"
              className="carousel-arrow is-next"
              onClick={next}
              disabled={!infinite && index === count - 1}
              aria-label="Next slide"
            >
              <AiOutlineRight />
            </button>
          </>
        )}

        {dots && count > 1 && (
          <ul className={cx('carousel-dots', dotsClassName)}>
            {slides.map((_, i) => (
              <li key={i}>
                <button
                  type="button"
                  className={cx('carousel-dot', i === index && 'is-active')}
                  onClick={() => goTo(i)}
                  aria-label={`Go to slide ${i + 1}`}
                  aria-current={i === index}
                />
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  }
);

Carousel.displayName = 'Carousel';

export default Carousel;
